import React, { useEffect, useState } from 'react';
import { Theme } from '@material-ui/core';
import { useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import config from '../../config.json';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    minHeight: '100vh',
    backgroundColor: '#d6e8f5',
    //fontFamily: '"MS PGothic", "Hiragino Kaku Gothic ProN", Arial, sans-serif',
    fontSize: '16px',
    color: '#333',
  },
  // Header bar: text title on light blue background
  pageTitle: {
    backgroundColor: '#d6e8f5',
    padding: theme.spacing(1.5, 3),
    fontSize: '16px',
    color: '#333',
  },
  // Content wrapper with padding
  contentWrapper: {
    maxWidth: 760,
    width: '100%',
    margin: '0 auto',
    padding: theme.spacing(0, 3),
  },
  // Blue section — outer: full-width background; inner table controlled by contentWrapper
  memberInfoSection: {
    backgroundColor: '#1a75bb',
    width: '100%',
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    marginBottom: 0,
  },
  memberInfoTable: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  memberInfoLabelCell: {
    backgroundColor: '#76acd6',
    color: '#fff',
    padding: theme.spacing(1, 2),
    width: '30%',
    fontSize: '16px',
    fontWeight: 'normal',
    border: '1px solid #5a94c2',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
  },
  memberInfoValueCell: {
    backgroundColor: '#fff',
    color: '#333',
    padding: theme.spacing(1, 2),
    fontSize: '16px',
    border: '1px solid #5a94c2',
    verticalAlign: 'middle',
  },
  // Card info section below the blue box
  cardInfoSection: {
    padding: theme.spacing(2, 3),
  },
  cardInfoTitle: {
    textAlign: 'center',
    fontSize: '16px',
    color: '#333',
    marginBottom: theme.spacing(1.5),
    borderBottom: '1px solid #aaa',
    paddingBottom: theme.spacing(0.5),
  },
  cardFieldLabel: {
    fontSize: '16px',
    color: '#333',
    marginBottom: theme.spacing(0.5),
    fontWeight: 'normal',
  },
  cardFieldValue: {
    fontSize: '16px',
    color: '#333',
    marginBottom: theme.spacing(0.25),
    borderBottom: '1px solid #aaa',
    paddingBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(0.5),
  },
  // Error section
  errorSection: {
    backgroundColor: '#d6e8f5',
    padding: theme.spacing(2, 3),
  },
  errorTitleBar: {
    backgroundColor: '#1a75bb',
    color: '#fff',
    padding: theme.spacing(0.75, 1.5),
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: 0,
  },
  errorBody: {
    backgroundColor: '#fff',
    borderLeft: '1px solid #aac8e0',
    borderRight: '1px solid #aac8e0',
    borderBottom: '1px solid #aac8e0',
    padding: theme.spacing(1, 1.5),
    fontSize: '0.8rem',
    color: '#555',
  },
}));

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function CardRegistrationResultGMO() {
  const classes = useStyles();
  const query = useQuery();
  const location = useLocation();

  const token = query.get('token');

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    const domainApi = config.ReUrlCardRegistration;
    fetch(`${domainApi}/api/CreditMember/card-registration-result/${token}`)
      .then(res => res.json())
      .then(result => {
        if (result.success && result.data) {
          try {
            const parsedData = JSON.parse(result.data);
            const actualData = parsedData.memberresult ? parsedData.memberresult : parsedData;
            setData(actualData);
          } catch (e) {
            console.error('Error parsing JSON from Backend', e);
          }
        }
      })
      .catch(err => {
        console.error('Error calling API', err);
      })
      .finally(() => {
        setLoading(false);
        // Clean URL to prevent F5 reading the old token
        if (location.search) {
          window.history.replaceState({}, document.title, location.pathname);
        }
      });
  }, [token, location]);

  const getExpiryDate = () => {
    if (data?.card?.expireMonth && data?.card?.expireYear) {
      return `${data.card.expireMonth} / ${data.card.expireYear}`;
    }
    if (data?.Expire && data?.Expire?.length === 4) {
      // GMO format: YYMM → MM / YYYY
      const yy = data.Expire.substring(0, 2);
      const mm = data.Expire.substring(2, 4);
      return `${mm} / 20${yy}`;
    }
    return '';
  };

  if (loading) {
    return (
      <Box className={classes.root} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Box style={{ textAlign: 'center', paddingTop: 60 }}>
          <CircularProgress size={32} style={{ color: '#1a75bb' }} />
          <Typography variant="body2" style={{ marginTop: 12, color: '#555' }}>
            処理中です...
          </Typography>
        </Box>
      </Box>
    );
  }

  // No data → token expired or F5 reload
  if (!data) {
    return (
      <Box className={classes.root}>
        <Box style={{ width: '100%', margin: '0 auto', padding: '16px 10px' }}>
          <Box className={classes.errorTitleBar}>クレジットカード編集に失敗しました。</Box>
          <Box className={classes.errorBody}>
            戻る・更新等の誤ったブラウザ操作を検知しました。初めからやり直すか、ほかのブラウザをご利用ください。
          </Box>
        </Box>
      </Box>
    );
  }

  const isSuccess = data.Result === 'SUCCESS' || data.Result === '1';

  if (!isSuccess) {
    return (
      <Box className={classes.root}>
        <Box className={classes.errorSection}>
          <Box className={classes.errorTitleBar}>クレジットカードの登録に失敗しました</Box>
          <Typography variant="body1" style={{ color: '#333', fontWeight: 'bold' }}>
            エラーコード: {data.ErrCode || '不明'}
          </Typography>
          <Typography variant="body2" style={{ marginTop: 8, color: '#333' }}>
            {data.ErrInfo || '入力情報を確認し、再度お試しください。'}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {/* Title */}
      <Box className={classes.contentWrapper}>
        <Box className={classes.pageTitle}>以下の内容でクレジットカードを登録しました。</Box>
      </Box>

      {/* Blue member info section — background full-width, table co vào theo contentWrapper */}
      <Box className={classes.memberInfoSection}>
        <Box className={classes.contentWrapper}>
          <table className={classes.memberInfoTable}>
            <tbody>
              <tr>
                <td className={classes.memberInfoLabelCell}>会員ID</td>
                <td className={classes.memberInfoValueCell}>{data.MemberID || '---'}</td>
              </tr>
              <tr>
                <td className={classes.memberInfoLabelCell}>氏名</td>
                <td className={classes.memberInfoValueCell}>{data.MemberName || '---'}</td>
              </tr>
            </tbody>
          </table>
        </Box>
      </Box>

      {/* Card info section */}
      <Box className={classes.contentWrapper}>
        <Box className={classes.cardInfoSection}>
          <Box className={classes.cardInfoTitle}>クレジットカード情報</Box>

          <Box style={{ maxWidth: 300 }}>
            <Typography className={classes.cardFieldLabel}>クレジットカード番号</Typography>
            <Typography className={classes.cardFieldValue}>{data.CardNo || '**** **** **** ****'}</Typography>

            <Box style={{ marginTop: 12 }}>
              <Typography className={classes.cardFieldLabel}>有効期限（月 / 年）</Typography>
              <Typography className={classes.cardFieldValue}>{getExpiryDate() || '** / ****'}</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
